import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../AuthContext";
import { LOGO_URL, BRAND } from "../brand";
/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const IN_APP_RE = /Instagram|FBAN|FBAV|FB_IAB|WhatsApp|Line\/|TikTok|Snapchat|; wv\)/i;

function googleLogin() {
  if (IN_APP_RE.test(navigator.userAgent || "")) {
    toast.error("O Google não permite login dentro do Instagram/WhatsApp. Abra o site no Chrome ou Safari (menu ⋮ > Abrir no navegador).", { duration: 9000 });
    return;
  }
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export function LoginPage() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    const r = await login(form.email, form.password);
    setLoading(false);
    if (r.ok) {
      toast.success("Bem-vinda de volta!");
      nav(["admin","admin_admin","admin_caixa"].includes(r.user?.role) ? "/admin" : "/perfil");
    } else setErr(r.error);
  };

  return (
    <div className="max-w-md mx-auto px-6 py-14">
      <div className="card-arya p-8">
        <div className="flex flex-col items-center gap-2 mb-6">
          <img src={LOGO_URL} alt={BRAND.name} className="w-20 h-20 object-contain"/>
          <h1 className="font-serif text-3xl">Entrar</h1>
          <p className="text-sm text-[color:var(--ink-500)]">Bem-vinda de volta ao Arya Studio</p>
        </div>
        <button onClick={googleLogin} className="w-full btn-ghost mb-4 flex items-center justify-center gap-2" data-testid="btn-google-login">
          <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 5c1.7 0 3.2.6 4.4 1.7l3.3-3.3C17.5 1.3 14.9 0 12 0 7.4 0 3.4 2.6 1.4 6.4l3.9 3C6.3 6.5 8.9 5 12 5z"/><path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5c-.3 1.5-1.2 2.8-2.5 3.7l3.8 3c2.3-2.1 3.7-5.3 3.7-8.9z"/><path fill="#FBBC05" d="M5.3 14.4A7 7 0 015 12c0-.8.1-1.6.4-2.4l-3.9-3C.5 8.3 0 10.1 0 12c0 1.9.5 3.7 1.4 5.3l3.9-2.9z"/><path fill="#34A853" d="M12 24c3 0 5.6-1 7.4-2.8l-3.8-3c-1 .7-2.4 1.2-3.6 1.2-3.1 0-5.7-1.5-6.7-4.3l-3.9 3C3.4 21.4 7.4 24 12 24z"/></svg>
          Entrar com Google
        </button>
        <div className="divider-ornament my-4 text-xs">ou com email</div>
        <form onSubmit={submit} className="space-y-3">
          <input required type="email" placeholder="Email" data-testid="input-login-email"
                 value={form.email} onChange={e=>setForm({...form, email:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3 outline-none focus:border-[color:var(--sun-500)]"/>
          <input required type="password" placeholder="Senha" data-testid="input-login-password"
                 value={form.password} onChange={e=>setForm({...form, password:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3 outline-none focus:border-[color:var(--sun-500)]"/>
          {err && <p className="text-sm text-[color:var(--danger)]" data-testid="login-error">{err}</p>}
          <button disabled={loading} className="btn-gold w-full" data-testid="btn-login-submit">
            {loading ? "Entrando..." : "Entrar"}
          </button>
        </form>
        <p className="text-sm text-center mt-4 text-[color:var(--ink-500)]">
          Novo por aqui? <Link to="/cadastro" className="text-[color:var(--sun-700)] underline" data-testid="link-to-register">Criar conta</Link>
        </p>
      </div>
    </div>
  );
}

export function RegisterPage() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", birthday: "",
    referral_code: params.get("ref") || "",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    const r = await register(form);
    setLoading(false);
    if (r.ok) { toast.success("Conta criada com sucesso!"); nav("/perfil"); }
    else setErr(r.error);
  };

  return (
    <div className="max-w-md mx-auto px-6 py-14">
      <div className="card-arya p-8">
        <div className="flex flex-col items-center gap-2 mb-6">
          <img src={LOGO_URL} alt={BRAND.name} className="w-20 h-20 object-contain"/>
          <h1 className="font-serif text-3xl">Criar conta</h1>
          <p className="text-sm text-[color:var(--ink-500)]">Junte-se ao Clube Arya</p>
        </div>
        <button onClick={googleLogin} className="w-full btn-ghost mb-4 flex items-center justify-center gap-2" data-testid="btn-google-register">
          <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 5c1.7 0 3.2.6 4.4 1.7l3.3-3.3C17.5 1.3 14.9 0 12 0 7.4 0 3.4 2.6 1.4 6.4l3.9 3C6.3 6.5 8.9 5 12 5z"/><path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5c-.3 1.5-1.2 2.8-2.5 3.7l3.8 3c2.3-2.1 3.7-5.3 3.7-8.9z"/><path fill="#FBBC05" d="M5.3 14.4A7 7 0 015 12c0-.8.1-1.6.4-2.4l-3.9-3C.5 8.3 0 10.1 0 12c0 1.9.5 3.7 1.4 5.3l3.9-2.9z"/><path fill="#34A853" d="M12 24c3 0 5.6-1 7.4-2.8l-3.8-3c-1 .7-2.4 1.2-3.6 1.2-3.1 0-5.7-1.5-6.7-4.3l-3.9 3C3.4 21.4 7.4 24 12 24z"/></svg>
          Cadastrar com Google
        </button>
        <div className="divider-ornament my-4 text-xs">ou com email</div>
        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Nome completo" data-testid="input-reg-name"
                 value={form.name} onChange={e=>setForm({...form, name:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3"/>
          <input required type="email" placeholder="Email" data-testid="input-reg-email"
                 value={form.email} onChange={e=>setForm({...form, email:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3"/>
          <input placeholder="Telefone (opcional)" data-testid="input-reg-phone"
                 value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3"/>
          <label className="text-[10px] tracking-widest text-[color:var(--sun-700)] font-display">DATA DE ANIVERSÁRIO</label>
          <input type="date" placeholder="Aniversário" data-testid="input-reg-birthday"
                 value={form.birthday} onChange={e=>setForm({...form, birthday:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3"/>
          <input required type="password" placeholder="Senha" data-testid="input-reg-password"
                 value={form.password} onChange={e=>setForm({...form, password:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3"/>
          <input placeholder="Código de indicação (opcional)" data-testid="input-reg-referral"
                 value={form.referral_code} onChange={e=>setForm({...form, referral_code:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3"/>
          {err && <p className="text-sm text-[color:var(--danger)]" data-testid="register-error">{err}</p>}
          <button disabled={loading} className="btn-gold w-full" data-testid="btn-register-submit">
            {loading ? "Criando..." : "Criar minha conta"}
          </button>
        </form>
        <p className="text-sm text-center mt-4 text-[color:var(--ink-500)]">
          Já tem conta? <Link to="/login" className="text-[color:var(--sun-700)] underline" data-testid="link-to-login">Entrar</Link>
        </p>
      </div>
    </div>
  );
}
